// web_components/wcProduct/wcProduct_Settings.ts

/**
 * Represents visibility settings for different elements within a web component.
 * Each element is associated with a boolean value indicating whether it should be visible.
 */
export interface VisibilitySettings {
	[element: string]: boolean;
}

/**
 * Represents environment-specific visibility settings for web components.
 * The settings object uses website identifiers as keys and `VisibilitySettings` objects as values.
 * This allows for different visibility configurations based on the environment or domain.
 */
export interface EnvironmentVisibilitySettings {
	[website: string]: VisibilitySettings;
}

/**
 * Represents product type-specific visibility settings for web components.
 * The settings object uses product type identifiers as keys and `VisibilitySettings` objects as values.
 * This allows for different visibility configurations based on the type of product being displayed.
 */
export interface ProductTypeVisibilitySettings {
	[productType: string]: VisibilitySettings;
}

/**
 * Configuration object defining visibility settings based on the website environment.
 * Each key represents a website, and the value is a `VisibilitySettings` object specifying
 * which elements should be visible or hidden for that specific environment.
 */
export const environmentVisibilitySettings: EnvironmentVisibilitySettings = {
	vwshop: {
		'wc-product__more-link': true,
	},
	dmshop: {
		'wc-product__more-link': true,
	},
	wwshop: {
		'wc-product__case-size': true,
		'wc-product__atb-select-btn': true,
	},
	default: {
		test: false,
	},
};

/**
 * Configuration object defining visibility settings based on the product type.
 * Each key represents a product type, and the value is a `VisibilitySettings` object specifying
 * which elements should be visible or hidden for that specific product type.
 */
export const productTypeVisibilitySettings: ProductTypeVisibilitySettings = {
	wine_bottles: {
		'wc-product__atb-select': false,
		'wc-product__more-link': false,
		'wc-product__case-size': false,
	},
	pre_mixed_wine_cases: {
		'wc-product__atb-qty': false,
		'wc-product__atb-select': false,
		'wc-product__country': false,
		'wc-product__star-rating': false,
		'wc-product__vegan': false,
	},
	composite_gifts: {
		'wc-product__atb-qty': false,
		'wc-product__star-rating': false,
		'wc-product__atb-select': false,
		'wc-product__case-size': false,
		'wc-product__more-link': false,
		'wc-product__vegan': false,
	},
	advent_calendar: {
		'wc-product__atb-qty': false,
		'wc-product__star-rating': false,
		'wc-product__atb-select': false,
		'wc-product__case-size': false,
		'wc-product__more-link': false,
		'wc-product__vegan': false,
	},
	default: {
		'wc-product__atb-select': false,
	},
};
